/* Admin → What We Offer Page Editor
 *
 * Three sections (Tools, Writing Aids, Backend Features).
 * Each section shows a list of feature cards (icon · title · body).
 * Admin can Add, Edit, or Delete cards.
 * Changes are saved to POST /api/admin/static/what-we-offer via PUT.
 * Reset returns to hardcoded defaults.
 *
 * Pattern matches the LandingPanel CMS approach.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader,
  AlertDialogTitle, AlertDialogDescription, AlertDialogFooter,
  AlertDialogCancel, AlertDialogAction,
} from '@/components/ui/alert-dialog';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Loader2, Plus, Pencil, Trash2, RotateCcw, Save, ChevronDown, ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';
import { PanelHeader } from './_shared';

const API = process.env.REACT_APP_BACKEND_URL;

// ─── Utility ─────────────────────────────────────────────────────────────────

function iconUrl(iconName) {
  // Just render the icon name as text — we don't want to eval arbitrary Lucide names.
  return iconName || '—';
}

// ─── SectionEditor ───────────────────────────────────────────────────────────

function SectionEditor({ title, items, onAdd, onEdit, onDelete, collapsed, onToggle }) {
  return (
    <div className="rounded-md border border-border bg-card">
      {/* Section header */}
      <button
        className="flex w-full items-center justify-between px-4 py-3 text-left"
        onClick={onToggle}
        data-testid={`wwo-section-toggle-${title.toLowerCase().replace(/\s+/g, '-')}`}
      >
        <span className="text-[13px] font-semibold">{title}</span>
        <div className="flex items-center gap-2">
          <span className="rounded-full bg-secondary px-2 py-0.5 text-[10px] text-muted-foreground">
            {items.length} cards
          </span>
          {collapsed
            ? <ChevronRight className="h-4 w-4 text-muted-foreground" />
            : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
        </div>
      </button>

      {!collapsed && (
        <div className="border-t border-border px-4 pb-4 pt-3 space-y-2">
          {items.map((item, idx) => (
            <div
              key={idx}
              className="flex items-start gap-3 rounded-md border border-border bg-background px-3 py-2"
              data-testid={`wwo-card-${idx}`}
            >
              {/* Icon chip */}
              <span className="mt-0.5 inline-flex shrink-0 rounded bg-secondary px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">
                {item.icon || '—'}
              </span>
              {/* Title + body */}
              <div className="flex-1 min-w-0">
                <p className="text-[12.5px] font-medium text-foreground">{item.title}</p>
                <p className="text-[11px] text-muted-foreground line-clamp-2 mt-0.5">{item.body}</p>
              </div>
              {/* Actions */}
              <div className="flex shrink-0 gap-1">
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-7 w-7"
                  onClick={() => onEdit(idx)}
                  data-testid={`wwo-edit-${idx}`}
                >
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-7 w-7 text-destructive hover:text-destructive"
                  onClick={() => onDelete(idx)}
                  data-testid={`wwo-delete-${idx}`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          ))}

          <Button
            size="sm"
            variant="outline"
            className="mt-1 gap-1.5"
            onClick={onAdd}
            data-testid={`wwo-add-${title.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <Plus className="h-3.5 w-3.5" /> Add card
          </Button>
        </div>
      )}
    </div>
  );
}

// ─── Card Edit Dialog ─────────────────────────────────────────────────────────

function CardEditDialog({ open, initial, onSave, onClose }) {
  const [form, setForm] = useState({ icon: '', title: '', body: '' });

  useEffect(() => {
    if (open) setForm(initial || { icon: '', title: '', body: '' });
  }, [open, initial]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="wwo-card-dialog">
        <DialogHeader>
          <DialogTitle>{initial ? 'Edit card' : 'Add card'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-1">
          <div>
            <Label className="text-[11px]">Lucide icon name</Label>
            <Input
              className="mt-1 font-mono text-[12px]"
              placeholder="e.g. PenTool, Wand2, Cpu"
              value={form.icon}
              onChange={set('icon')}
              data-testid="wwo-card-icon"
            />
            <p className="mt-0.5 text-[10px] text-muted-foreground">
              Any name from{' '}
              <a href="https://lucide.dev/icons/" target="_blank" rel="noreferrer"
                className="underline">lucide.dev/icons</a>.
            </p>
          </div>
          <div>
            <Label className="text-[11px]">Title</Label>
            <Input
              className="mt-1 text-[12px]"
              placeholder="Feature title"
              value={form.title}
              onChange={set('title')}
              data-testid="wwo-card-title"
            />
          </div>
          <div>
            <Label className="text-[11px]">Body copy</Label>
            <Textarea
              className="mt-1 text-[12px]"
              rows={4}
              placeholder="Short description of the feature…"
              value={form.body}
              onChange={set('body')}
              data-testid="wwo-card-body"
            />
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
          <Button
            size="sm"
            onClick={() => { if (form.title.trim()) onSave(form); }}
            disabled={!form.title.trim()}
            data-testid="wwo-card-save"
          >
            Save card
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Main Panel ───────────────────────────────────────────────────────────────

// ─── Text fields config ───────────────────────────────────────────────────────
// Maps content keys → human labels. All are plain string fields on WhatWeOfferContent.
const TEXT_FIELDS = [
  { key: 'page_intro',          label: 'Page Intro',                rows: 3 },
  { key: 'who_we_are_body',     label: 'Who We Are',                rows: 4 },
  { key: 'tools_section_desc',  label: 'Tools — section intro',     rows: 2 },
  { key: 'aids_section_desc',   label: 'Writing Aids — section intro', rows: 2 },
  { key: 'backend_section_desc',label: 'Backend — section intro',   rows: 2 },
];

const SECTION_KEYS = [
  { key: 'tools',            label: 'Tools' },
  { key: 'aids',             label: 'Writing Aids' },
  { key: 'backend_features', label: 'Backend Features' },
];

export default function WhatWeOfferPanel() {
  const [data, setData]       = useState(null);  // merged content from API
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [collapsed, setCollapsed] = useState({ tools: false, aids: true, backend_features: true });
  const [textOpen, setTextOpen]   = useState(false);  // toggle for page-text section
  const [textDraft, setTextDraft] = useState({});      // local draft of text fields

  // Card editor state
  const [editState, setEditState] = useState(null); // {sectionKey, idx (null=add), item}

  // ── Load ──────────────────────────────────────────────────────────────────
  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/admin/static/what-we-offer`, { withCredentials: true });
      setData(r.data);
      // Seed textDraft from loaded content
      const c = r.data?.content ?? {};
      const draft = {};
      TEXT_FIELDS.forEach(({ key }) => { draft[key] = c[key] ?? ''; });
      setTextDraft(draft);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load What We Offer content.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  // ── Helpers ───────────────────────────────────────────────────────────────

  const getSection = (key) => data?.content?.[key] ?? [];

  const putContent = async (contentPatch) => {
    setSaving(true);
    try {
      const current = data?.content ?? {};
      const merged  = { ...current, ...contentPatch };
      await axios.put(`${API}/api/admin/static/what-we-offer`,
        { content: merged },
        { withCredentials: true });
      setData((prev) => ({ ...prev, content: merged }));
      toast.success('Saved.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  // ── Save all text fields at once ─────────────────────────────────────────
  const saveTextFields = async () => {
    await putContent(textDraft);
    // Note: putContent already shows a "Saved." toast — no second toast here.
  };

  // ── Section actions ───────────────────────────────────────────────────────

  const handleDelete = (sectionKey, idx) => {
    const next = [...getSection(sectionKey)];
    next.splice(idx, 1);
    putContent({ [sectionKey]: next });
  };

  const handleSaveCard = (form) => {
    const { sectionKey, idx } = editState;
    const next = [...getSection(sectionKey)];
    if (idx === null) {
      next.push(form);
    } else {
      next[idx] = form;
    }
    putContent({ [sectionKey]: next });
    setEditState(null);
  };

  // ── Reset ─────────────────────────────────────────────────────────────────

  const handleReset = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/api/admin/static/what-we-offer/reset`, {}, { withCredentials: true });
      await reload();
      toast.success('Reset to factory defaults.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reset failed.');
    } finally {
      setSaving(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6" data-testid="what-we-offer-panel">
      <PanelHeader
        title="What We Offer — Feature Cards"
        subtitle="Add, edit, or remove the feature cards shown in each section of /what-we-offer. Changes go live immediately."
        right={
          <div className="flex items-center gap-2">
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" size="sm" className="gap-1.5" disabled={saving}>
                  <RotateCcw className="h-3.5 w-3.5" /> Reset to defaults
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Reset to factory defaults?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will discard all edits and restore the original 25 feature cards.
                    This action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleReset}>Reset</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
            {saving && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
          </div>
        }
      />

      <div className="space-y-3">
        {/* ── Page Text Section ── */}
        <div className="rounded-md border border-border bg-card">
          <button
            className="flex w-full items-center justify-between px-4 py-3 text-left"
            onClick={() => setTextOpen((o) => !o)}
            data-testid="wwo-text-toggle"
          >
            <span className="text-[13px] font-semibold">Page Text</span>
            <div className="flex items-center gap-2">
              <span className="rounded-full bg-secondary px-2 py-0.5 text-[10px] text-muted-foreground">
                intros &amp; body copy
              </span>
              {textOpen
                ? <ChevronDown className="h-4 w-4 text-muted-foreground" />
                : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
            </div>
          </button>
          {textOpen && (
            <div className="border-t border-border px-4 pb-4 pt-3 space-y-3">
              {TEXT_FIELDS.map(({ key, label, rows }) => (
                <div key={key}>
                  <Label className="text-[11px]">{label}</Label>
                  <Textarea
                    className="mt-1 text-[12px]"
                    rows={rows}
                    value={textDraft[key] ?? ''}
                    onChange={(e) => setTextDraft((d) => ({ ...d, [key]: e.target.value }))}
                    data-testid={`wwo-text-${key}`}
                  />
                </div>
              ))}
              <Button
                size="sm"
                className="gap-1.5"
                onClick={saveTextFields}
                disabled={saving}
                data-testid="wwo-text-save"
              >
                {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                Save text
              </Button>
            </div>
          )}
        </div>

        {/* ── Feature Card Sections ── */}
        {SECTION_KEYS.map(({ key, label }) => (
          <SectionEditor
            key={key}
            title={label}
            items={getSection(key)}
            collapsed={collapsed[key]}
            onToggle={() => setCollapsed((s) => ({ ...s, [key]: !s[key] }))}
            onAdd={() => setEditState({ sectionKey: key, idx: null, item: null })}
            onEdit={(idx) => setEditState({ sectionKey: key, idx, item: getSection(key)[idx] })}
            onDelete={(idx) => handleDelete(key, idx)}
          />
        ))}
      </div>

      {/* Card edit/add dialog */}
      <CardEditDialog
        open={!!editState}
        initial={editState?.item}
        onSave={handleSaveCard}
        onClose={() => setEditState(null)}
      />
    </div>
  );
}
